/*
  <bloom-fit-finder> — a lightweight guided fit quiz (homepage teaser).
  Progressive enhancement: all questions are server-rendered; with JS off the
  form is a normal, crawlable set of questions plus a size-guide link. JS turns it
  into a stepped quiz, plays a brief "matching" moment, then computes a
  recommendation from a map passed via data-products. Emits fit_finder_* events.
*/
class BloomFitFinder extends HTMLElement {
  connectedCallback() {
    try {
      this.products = JSON.parse(this.dataset.products || '{}');
    } catch (e) {
      this.products = {};
    }
    this.steps = Array.from(this.querySelectorAll('[data-step]'));
    this.progress = Array.from(this.querySelectorAll('[data-progress] span'));
    this.result = this.querySelector('[data-result]');
    this.matching = this.querySelector('[data-matching]');
    this.backBtn = this.querySelector('[data-back]');
    this.restartBtn = this.querySelector('[data-restart]');
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.current = 0;
    this.answers = {};

    if (!this.steps.length) return;
    this.classList.add('bloom-fit--js');

    this.addEventListener('change', (e) => {
      if (e.target.matches('input[type="radio"]')) this.onAnswer(e.target);
    });
    if (this.backBtn) this.backBtn.addEventListener('click', () => this.goBack());
    if (this.restartBtn) this.restartBtn.addEventListener('click', () => this.restart());

    this.show(0);
    this.track('fit_finder_start', {});
  }

  onAnswer(input) {
    const step = input.closest('[data-step]');
    this.answers[step.dataset.key] = input.value;
    this.track('fit_finder_answer', { question: step.dataset.key, answer: input.value });
    const next = this.current + 1;
    // brief pause so the selection is visible before advancing
    window.setTimeout(() => {
      if (next < this.steps.length) this.show(next);
      else this.finish();
    }, 240);
  }

  show(i) {
    this.current = i;
    this.steps.forEach((s, idx) => (s.hidden = idx !== i));
    this.progress.forEach((p, idx) => p.classList.toggle('is-active', idx <= i));
    if (this.backBtn) this.backBtn.hidden = i === 0;
    if (this.result) this.result.hidden = true;
    if (this.matching) this.matching.hidden = true;
  }

  goBack() {
    if (this.current > 0) this.show(this.current - 1);
  }

  restart() {
    this.answers = {};
    this.querySelectorAll('input[type="radio"]').forEach((r) => (r.checked = false));
    const bar = this.result && this.result.querySelector('[data-confidence-bar]');
    if (bar) bar.style.width = '0';
    this.show(0);
    this.track('fit_finder_restart', {});
  }

  finish() {
    this.steps.forEach((s) => (s.hidden = true));
    if (this.backBtn) this.backBtn.hidden = true;
    this.progress.forEach((p) => p.classList.add('is-active'));
    if (this.result) this.result.hidden = true;

    if (this.matching && !this.reduce) {
      this.matching.hidden = false;
      window.clearTimeout(this._matchT);
      this._matchT = window.setTimeout(() => {
        this.matching.hidden = true;
        this.showResult();
      }, 1000);
    } else {
      if (this.matching) this.matching.hidden = true;
      this.showResult();
    }
  }

  showResult() {
    const goal = this.answers.goal || 'full-body';
    const product = this.products[goal] || Object.values(this.products)[0] || {};
    const compression = this.answers.compression || 'medium';
    const outfit = this.answers.outfit || '';

    let sizeHint = 'Order your usual size.';
    let confidence = 86;
    if (compression === 'light') {
      sizeHint = 'Consider sizing up for lighter, everyday compression.';
      confidence = 80;
    } else if (compression === 'firm') {
      sizeHint = 'Stay true to size for firm, sculpting compression.';
      confidence = 90;
    }

    const outfitReason = {
      dress: ' under fitted dresses',
      pants: ' under pants and jeans',
      'low-back': ' with low-back or low-neck outfits',
      everyday: ' for everyday wear',
    };
    const reason = `Based on your goal and preferred compression, this is your best match${
      outfitReason[outfit] || ''
    }. ${sizeHint}`;

    if (!this.result) return;
    this.result.querySelector('[data-result-name]').textContent = product.name || 'Your match';
    this.result.querySelector('[data-result-reason]').textContent = reason;
    const bar = this.result.querySelector('[data-confidence-bar]');
    const val = this.result.querySelector('[data-confidence-val]');
    if (bar) { bar.style.width = '0'; window.requestAnimationFrame(() => (bar.style.width = confidence + '%')); }
    if (val) val.textContent = confidence + '% match';
    const cta = this.result.querySelector('[data-result-cta]');
    if (cta && product.url) cta.href = product.url;

    const mediaWrap = this.result.querySelector('[data-result-media]');
    const img = this.result.querySelector('[data-result-img]');
    const hasImg = !!(product && product.img);
    if (mediaWrap && img) {
      if (hasImg) {
        img.src = product.img;
        img.alt = product.name || '';
        mediaWrap.hidden = false;
      } else {
        mediaWrap.hidden = true;
      }
    }
    this.result.classList.toggle('bloom-fit__result--has-media', hasImg);

    this.result.hidden = false;
    this.result.focus?.();

    this.track('fit_finder_complete', { product: product.name, confidence: confidence, compression });
  }

  track(name, detail) {
    this.dispatchEvent(new CustomEvent('bloom:' + name, { bubbles: true, detail }));
  }
}

if (!customElements.get('bloom-fit-finder')) {
  customElements.define('bloom-fit-finder', BloomFitFinder);
}
